/**
 * THE CALL DESK: every call, and the one thing to do about it.
 *
 * JEV for New York Intelligence Agency, 2026-10-15 (D20): "Neelum wants the
 * system stripped down to only: phone number, name, phone recording, phone
 * transcript, and SEND AGREEMENT button. Remove all car-related language
 * and extra features. They want to start fresh with just this minimal
 * list, no cascading workflows yet."
 *
 * So this is the whole app for that shop: five things per call and
 * nothing else on the screen. Which shops get it is src/domain/calldesk.ts.
 * D21 (same day) added a notes box open on every row and Report spam in
 * each row's upper right, which blocks the number.
 *
 * The list comes from the server (server/routes/calldesk.ts), not from the
 * database directly, because a technician login reads almost nothing of
 * comm_events, and Laura was given this screen to work from.
 *
 * The Play and Read buttons are the ones every other screen uses
 * (src/ui/callplay.ts). The agreement is the job card's agreement card,
 * pointed at a caller (src/pages/esign-card.ts). One open at a time,
 * because that card's fields are found by id.
 */

import { api, db } from "../data/client.ts";
import { esc, mount, since } from "../ui/dom.ts";
import { callButtonsHtml, wireCallButtons } from "../ui/callplay.ts";
import { mountEsignForCaller } from "./esign-card.ts";
import { mountContact, payInfoEditor, whenDetailsSaved, type Reach } from "./calldesk-contact.ts";
import type { PayMethod } from "../domain/paymethods.ts";
import { showNumber } from "../domain/countrycodes.ts";
import type { Logistics } from "../domain/staffjob.ts";
import { assignedPill, saveLogistics, setJob, setStaff, staffClick, staffEditor, staffHtml,
  type Staff, type StaffAsk } from "./calldesk-staff.ts";

type Call = { id: string; at: string; phone10: string | null; name: string | null; secs: number | null;
  missed: boolean; call_id: string | null; team?: boolean; calls?: number;
  said?: { at: string; text: string; via: string } | null;
  agreement?: { status: string; job_id: string | null } | null; note?: string;
  pay_sent?: PaySent[]; payments?: Payment[]; reach?: Reach | null;
  logistics?: Logistics | null; asks?: StaffAsk[]; lead?: boolean };
type PaySent = { id: string; method: string; sent_at: string };
type Payment = { id: string; confirmed_at: string | null; amount: string };

const phoneFmt = (p: string | null): string =>
  p && p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : "No number";
const length = (s: number | null): string =>
  !s ? "" : s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;

/* Kept across visits, so opening a recording and coming back keeps the search. */
let query = "";
/* Where the list on screen starts and where the next Show more reads from.
   A date on the side rail moves both (Neelum, 2026-10-29). */
let start = 0;
let next = 0;
/* The Leads tab is this screen with only the callers marked Lead. */
let leadsOnly = false;
type DayMark = { d: string; i: number };
let days: DayMark[] = [];
const PAGE = 40;
const moreBtn = (more: boolean) => more ? `<button type="button" class="btn" id="cdmore">Show more</button>` : "";
const newerBtn = () => start > 0 ? `<button type="button" class="btn" id="cdnewer">Show newer</button>` : "";

/* MINIMIZED CARDS (Neelum, 2026-10-29: "a minimize button added for each
   caller card ... maybe we can decide which is most user friendly after a
   week"). Down to the name, number and when. Kept per person in their own
   browser: a preference, not data, so a private window losing it costs nothing. */
const MIN_KEY = "calldesk.min";
const minimized = (() => { try { return new Set<string>(JSON.parse(localStorage.getItem(MIN_KEY) ?? "[]") as string[]); } catch { return new Set<string>(); } })();
const keepMin = () => { try { localStorage.setItem(MIN_KEY, JSON.stringify([...minimized].slice(-2000))); } catch { /* kept for this visit only */ } };
/* THE DATES DOWN THE SIDE: Newest, each of the last ten days with callers,
   then one per month further back. Each one is where that day starts. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayLabel = (d: string): string => `${MONTHS[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}`;
function railHtml(): string {
  if (days.length < 2) return "";
  const marks: { label: string; i: number; title: string }[] = [{ label: "Newest", i: 0, title: "Back to the newest" }];
  const months = new Set<string>();
  days.forEach((m, n) => {
    if (n < 10) { if (n) marks.push({ label: dayLabel(m.d), i: m.i, title: dayLabel(m.d) }); months.add(m.d.slice(0, 7)); return; }
    const ym = m.d.slice(0, 7);
    if (months.has(ym)) return;
    months.add(ym);
    marks.push({ label: `${MONTHS[Number(ym.slice(5)) - 1]}${ym.slice(0, 4) === days[0]!.d.slice(0, 4) ? "" : ` '${ym.slice(2, 4)}`}`,
      i: m.i, title: `${MONTHS[Number(ym.slice(5)) - 1]} ${ym.slice(0, 4)}` });
  });
  const here = [...marks].reverse().find((m) => m.i <= start)?.i ?? 0;
  return `<nav class="cd-rail" aria-label="Jump to a date">${marks.map((m) =>
    `<button type="button" data-jump="${m.i}" title="${esc(m.title)}"${m.i === here ? ` aria-current="true"` : ""}>${esc(m.label)}</button>`).join("")}</nav>`;
}
/* The date box: the first caller on or before the day picked. */
const startFor = (d: string): number => days.find((m) => m.d <= d)?.i ?? (days[days.length - 1]?.i ?? 0);

/* The shop's payment methods, from the list read (Payment info at the top). */
let methods: PayMethod[] = [];
/* "Send to NK" on firms that pass hard callers to the owner (src/domain/calldesk.ts). */
let handoff: string | null = null;
/* Each caller's payment state, so one row redraws without the whole list. */
const payState = new Map<string, { name: string; sent: PaySent[]; payments: Payment[]; reach: Reach | null }>();

/* The number the desk set on a caller, or the one they came in on (0227). */
const reachOf = (p10: string): { cc: string; phone: string } => {
  const r = payState.get(p10)?.reach;
  return r?.phone ? { cc: r.cc, phone: r.phone } : { cc: "1", phone: p10 };
};
const numberLink = (p10: string): string => {
  const r = reachOf(p10);
  return `<a href="tel:+${esc(r.cc)}${esc(r.phone)}" data-number-of="${esc(p10)}">${esc(showNumber(r.cc, r.phone))}</a>`;
};

async function load(q: string, from = 0): Promise<{ calls: Call[]; more: boolean }> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  const r = await fetch(api(`/api/calldesk/calls?start=${from}${leadsOnly ? "&leads=1" : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`), {
    headers: { Authorization: `Bearer ${session?.access_token ?? ""}` },
  });
  const body = await r.json().catch(() => ({})) as { calls?: Call[]; more?: boolean; error?: string; pay_methods?: PayMethod[];
    staff?: Staff[]; firm?: string; handoff?: string | null; days?: DayMark[] };
  if (!r.ok) throw new Error(body.error ?? "The calls did not load.");
  methods = body.pay_methods ?? methods;
  handoff = body.handoff ?? null;
  days = body.days ?? days;
  setStaff(body.staff, body.firm);
  for (const c of body.calls ?? []) if (c.phone10) {
    payState.set(c.phone10, { name: c.name ?? "", sent: c.pay_sent ?? [], payments: c.payments ?? [], reach: c.reach ?? null });
    setJob(c.phone10, c.name ?? "", c.logistics, c.asks);
  }
  return { calls: body.calls ?? [], more: Boolean(body.more) };
}

/* Where the agreement stands, in the words on the card it opens. */
const AGREE: Record<string, string> = { sent: "Agreement sent", viewed: "Agreement opened",
  client_signed: "Signed", completed: "Signed and countersigned", declined: "Declined", expired: "Agreement expired" };

/* THE NOTES BOX, OPEN ON EVERY ROW (D21, Neelum via JEV, 2026-10-15: "A
   notes box visible on ALL call records (not just when you open one)").
   It was behind a tap on the row, so a note nobody had opened was a note
   nobody read. Leaving the box saves it, so forty open boxes do not each
   need their Save button found; the button stays for anyone who looks for
   it. Keyed by phone, so a caller with no number has nothing to key on. */
function notesBox(c: Call): string {
  if (!c.phone10) return "";
  const note = c.note ?? "";
  return `<div data-notes-for="${esc(c.id)}" style="margin:.4rem 0">
      <textarea rows="2" style="width:100%" placeholder="Notes on this caller" aria-label="Notes on this caller"
        data-note-was="${esc(note)}">${esc(note)}</textarea>
      <div class="row" style="gap:.4rem;align-items:center"><button type="button" class="btn sm" data-save-note="${esc(c.phone10)}">Save notes</button>
        <span class="fine quiet" data-note-msg></span></div>
    </div>`;
}

async function saveNote(box: HTMLElement): Promise<void> {
  const ta = box.querySelector("textarea");
  const btn = box.querySelector<HTMLButtonElement>("button[data-save-note]");
  const msg = box.querySelector<HTMLElement>("[data-note-msg]");
  if (!ta || !btn) return;
  const text = ta.value;
  btn.disabled = true; if (msg) msg.textContent = "Saving…";
  try {
    await post("/api/calldesk/note", { phone10: btn.dataset["saveNote"], body: text });
    ta.dataset["noteWas"] = text;
    if (msg) msg.textContent = "Saved.";
  } catch (err) { if (msg) msg.textContent = err instanceof Error ? err.message : String(err); }
  btn.disabled = false;
}

/**
 * THE PAYMENT ROW, under Send agreement (Neelum, 2026-10-17 9:39 PM): one
 * button per method; tapping one texts that method's info to the caller
 * and it stays green, with Undo in case it was the wrong one ("a client
 * needs to pay with more than one payment method", so any number can be
 * green). Under it, "Payment confirmed?" lines: the tick shows its date,
 * the box beside it holds the amount, and another line can be added for an
 * extension or a second payment.
 */
const dateOf = (iso: string): string => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

function payHtml(p10: string): string {
  const st = payState.get(p10);
  if (!st || !methods.length) return "";
  const btns = methods.map((m) => {
    const sent = st.sent.find((x) => x.method === m.key);
    return sent
      ? `<span class="row" style="gap:.2rem;align-items:center"><button type="button" class="btn sm" style="background:#1f8f4e;border-color:#1f8f4e;color:#fff"
          data-pay-send="${esc(m.key)}" title="Sent ${esc(dateOf(sent.sent_at))}">${esc(m.label)} &#10003;</button>
          <button type="button" class="btn ghost sm" data-pay-undo="${esc(sent.id)}" aria-label="Undo ${esc(m.label)}">Undo</button></span>`
      : `<button type="button" class="btn ghost sm" data-pay-send="${esc(m.key)}">${esc(m.label)}</button>`;
  }).join("");
  const rows = st.payments.length ? st.payments : [{ id: "", confirmed_at: null, amount: "" }];
  const lines = rows.map((r) => `<div class="row" style="gap:.5rem;align-items:center;flex-wrap:wrap" data-pay-line="${esc(r.id)}">
      <label class="fine" style="display:flex;gap:.35rem;align-items:center"><input type="checkbox" data-pay-ok${r.confirmed_at ? " checked" : ""}> Payment confirmed?</label>
      ${r.confirmed_at ? `<span class="fine ok">${esc(dateOf(r.confirmed_at))}</span>` : ""}
      <input type="text" inputmode="decimal" data-pay-amt value="${esc(r.amount)}" placeholder="Amount paid" aria-label="Amount paid" style="width:8rem" data-was="${esc(r.amount)}">
    </div>`).join("");
  return `<div class="fine" style="margin-top:.5rem"><b>Send payment info</b></div>
    <div class="row" style="gap:.35rem;flex-wrap:wrap">${btns}</div>
    <div style="display:grid;gap:.35rem;margin-top:.5rem">${lines}</div>
    <button type="button" class="btn ghost sm" data-pay-add style="margin-top:.35rem">+ Add another payment</button>
    <span class="fine" data-pay-msg></span>`;
}

function rowHtml(c: Call): string {
  const who = c.name ? esc(c.name) : `<span class="quiet">Name not on file</span>`;
  const how = c.calls ? `${c.calls} call${c.calls === 1 ? "" : "s"}` : (c.said?.via ?? "");
  const when = `${esc(since(c.at))}${how ? ` &middot; ${esc(how)}` : ""}${c.missed ? " &middot; last one missed" : ""}`;
  const ag = c.agreement;
  const signed = ag && (ag.status === "client_signed" || ag.status === "completed");
  /* The case is one tap from the row, and once they have signed it says
     what is next: they pay up front (Anthony, 2026-10-14). */
  const caseLink = ag?.job_id ? `<a class="btn ${signed ? "primary" : "ghost"} sm" href="#/job/${esc(ag.job_id)}">${
    signed ? "Take the payment" : "Open the case"}</a>` : "";
  /* Not offered on the team's own numbers (Jev's audit: David and Neelum
     testing the line showed as callers, one tap from an agreement made out
     to themselves). */
  const agree = c.phone10 && !c.team ? `<button type="button" class="btn ${signed ? "" : "primary"} sm" data-agree="${esc(c.id)}"
        data-phone="${esc(c.phone10)}" data-name="${esc(c.name ?? "")}">${ag ? "Agreement" : "Send agreement"}</button>` : "";
  /* REPORT SPAM, upper right where Neelum asked for it (2026-10-15). It
     blocks the number: server/routes/calldesk.ts. */
  /* CONTACT: call, text or email them, and the payment buttons
     (src/pages/calldesk-contact.ts, Neelum 2026-10-17). */
  const contact = c.phone10 && !c.team ? `<button type="button" class="btn sm" data-contact="${esc(c.id)}"
        data-phone="${esc(c.phone10)}" data-name="${esc(c.name ?? "")}">Contact</button>` : "";
  const spam = c.phone10 && !c.team ? `<button type="button" class="btn ghost danger sm" data-spam="${esc(c.phone10)}">Report spam</button>` : "";
  /* SEND TO NK, right under Report spam (Neelum, 2026-10-21). */
  const nk = c.phone10 && !c.team && handoff ? `<button type="button" class="btn sm" data-handoff="${esc(c.phone10)}"
        data-name="${esc(c.name ?? "")}">${esc(handoff)}</button>` : "";
  /* LEAD, beside Send to NK (Neelum, 2026-10-29): a good caller, onto the Leads tab.
     The three sit in one row under the name (Neelum, 2026-10-30: a column left too much empty space). */
  const lead = c.phone10 && !c.team ? leadBtn(c.phone10, !!c.lead) : "";
  const min = minimized.has(c.id);
  const minBtn = `<button type="button" class="btn ghost sm" data-min="${esc(c.id)}" aria-expanded="${!min}"
        aria-label="${min ? "Open" : "Minimize"} this caller" title="${min ? "Open" : "Minimize"}">${min ? "+" : "&minus;"}</button>`;
  return `<div class="card pad cd-card${min ? " cd-min" : ""}" data-row="${esc(c.id)}" style="cursor:pointer">
    <div class="row" data-head style="justify-content:space-between;align-items:flex-start;gap:.5rem">
      <div><b>${who}</b>${c.lead ? ` <span class="pill ok" data-lead-pill>Lead</span>` : ""}${c.team ? ` <span class="pill">Team</span>` : ""}${ag ? ` <span class="pill${signed ? " ok" : ""}">${esc(AGREE[ag.status] ?? ag.status)}</span>` : ""}${c.phone10 && !c.team ? assignedPill(c.phone10) : ""}
        <div>${c.phone10 ? numberLink(c.phone10) : esc(phoneFmt(null))}
        <span class="fine quiet">${when}</span></div></div>
      <div class="cd-acts">${minBtn}</div>
    </div>
    ${spam || nk || lead ? `<div class="row cd-flags" style="gap:.4rem;flex-wrap:wrap">${spam}${nk}${lead}</div>` : ""}
    ${c.said ? `<div class="fine"><b>${esc(c.said.via)}:</b> ${esc(c.said.text)}</div>` : ""}
    ${c.call_id ? callButtonsHtml(c.call_id) : c.calls ? `<div class="fine quiet">No answered call to play.</div>` : ""}
    ${notesBox(c)}
    ${caseLink || agree || contact ? `<div class="row" style="gap:.4rem;flex-wrap:wrap">${caseLink}${agree}${contact}</div>` : ""}
    ${c.phone10 && !c.team ? `<div class="calldesk-pay" data-pay-for="${esc(c.phone10)}">${payHtml(c.phone10)}</div>` : ""}
    ${c.phone10 && !c.team ? `<div class="cd-staff" data-staff-for="${esc(c.phone10)}">${staffHtml(c.phone10)}</div>` : ""}
    <div class="calldesk-esign" data-esign-for="${esc(c.id)}" hidden></div>
    <div class="calldesk-contact" data-contact-for="${esc(c.id)}" hidden style="margin-top:.5rem"></div>
  </div>`;
}

const leadBtn = (p10: string, on: boolean): string => on
  ? `<button type="button" class="btn sm" style="background:#1f8f4e;border-color:#1f8f4e;color:#fff" data-lead="${esc(p10)}" data-on="1"
      title="Marked a lead. Tap to take it off the Leads tab.">Lead &#10003;</button>`
  : `<button type="button" class="btn ghost sm" data-lead="${esc(p10)}">Lead</button>`;

async function post(path: string, payload: unknown): Promise<{ ok?: boolean; error?: string }> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  const r = await fetch(api(path), {
    method: "POST",
    headers: { Authorization: `Bearer ${session?.access_token ?? ""}`, "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await r.json().catch(() => ({})) as { ok?: boolean; error?: string };
  if (!r.ok) throw new Error(body.error ?? "That did not go through. Try again.");
  return body;
}

export async function callDeskPage(leads = false): Promise<void> {
  if (leads !== leadsOnly) { leadsOnly = leads; start = 0; }
  wireCallButtons();
  /* A number changed in the Contact box: the row's number and its payment
     texts follow it straight away. */
  whenDetailsSaved((p10, r) => {
    const st = payState.get(p10);
    if (st) st.reach = r;
    document.querySelectorAll<HTMLElement>(`[data-number-of="${CSS.escape(p10)}"]`).forEach((a) => { a.outerHTML = numberLink(p10); });
  });
  mount(`<div class="page">
    <header class="page-head"><h1>${leadsOnly ? "Leads" : "Calls"}</h1>
      <p>${leadsOnly ? "The callers marked Lead. Tap Lead &#10003; on a card to take it off."
        : "Who called, the recording, what was said, and the agreement."}</p></header>
    <form class="cx-search card" id="cdform" autocomplete="off">
      <input id="cdq" type="search" enterkeyhint="search" value="${esc(query)}"
        placeholder="Find a number or a name" aria-label="Find a number or a name">
      <button class="btn primary" type="submit">Find</button>
    </form>
    <div class="row" style="gap:.5rem;align-items:center;flex-wrap:wrap;margin:.5rem 0">
      <label class="fine" style="display:flex;gap:.35rem;align-items:center">Go to date
        <input type="date" id="cddate" aria-label="Go to date"></label>
      <button type="button" class="btn ghost sm" id="cdminall">Minimize all</button>
      <button type="button" class="btn ghost sm" id="cdopenall">Open all</button>
    </div>
    ${leadsOnly ? "" : `<div id="cdpay" hidden></div>
    <div id="cdstaff" hidden></div>`}
    <div id="cdrail"></div>
    <div id="cdbody" class="cd-list"><div class="loading">Reading the calls&hellip;</div></div>
  </div>`);

  const body = document.getElementById("cdbody");
  const rail = document.getElementById("cdrail");
  const drawRail = () => { if (rail) rail.innerHTML = railHtml(); };
  const draw = async () => {
    if (!body) return;
    try {
      const { calls, more } = await load(query, start);
      next = start + calls.length;
      body.innerHTML = calls.length ? newerBtn() + calls.map(rowHtml).join("") + moreBtn(more)
        : `<div class="card pad"><p class="quiet">${query ? "Nobody by that number or name."
          : leadsOnly ? "No leads yet. Tap Lead on a good caller's card in Calls and they show up here." : "No calls yet."}</p></div>`;
      drawRail();
    } catch (e) {
      body.innerHTML = `<div class="card pad"><p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p></div>`;
    }
  };

  document.getElementById("cdform")?.addEventListener("submit", (e) => {
    e.preventDefault();
    query = (document.getElementById("cdq") as HTMLInputElement).value.trim();
    start = 0;
    void draw();
  });

  /* A DATE: from the box, or a tap on the rail. The list starts there. */
  const jump = (i: number) => { start = Math.max(0, i); void draw().then(() => body?.scrollIntoView({ block: "start" })); };
  document.getElementById("cddate")?.addEventListener("change", (e) => {
    const d = (e.target as HTMLInputElement).value;
    if (d) jump(startFor(d));
  });
  rail?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>("button[data-jump]");
    if (b) jump(Number(b.dataset["jump"]) || 0);
  });
  const setAll = (min: boolean) => body?.querySelectorAll<HTMLElement>("[data-row]").forEach((row) => setMin(row, min));
  document.getElementById("cdminall")?.addEventListener("click", () => setAll(true));
  document.getElementById("cdopenall")?.addEventListener("click", () => setAll(false));

  body?.addEventListener("click", async (e) => {
    /* A PAGE AT A TIME. It stopped at 150 calls with no way past them. */
    const m = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>("#cdmore");
    if (m) {
      m.disabled = true; m.textContent = "Reading…";
      try {
        const { calls, more } = await load(query, next);
        next += calls.length;
        m.insertAdjacentHTML("beforebegin", calls.map(rowHtml).join(""));
        m.insertAdjacentHTML("afterend", moreBtn(more)); m.remove();
      } catch { m.disabled = false; m.textContent = "Show more"; }
      return;
    }
    /* SHOW NEWER, after a jump back: the callers between there and the top. */
    const nw = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>("#cdnewer");
    if (nw) {
      nw.disabled = true; nw.textContent = "Reading…";
      try {
        const from = Math.max(0, start - PAGE);
        const { calls } = await load(query, from);
        const fresh = calls.slice(0, start - from);
        start = from;
        nw.insertAdjacentHTML("afterend", fresh.map(rowHtml).join(""));
        nw.insertAdjacentHTML("beforebegin", newerBtn()); nw.remove();
        drawRail();
      } catch { nw.disabled = false; nw.textContent = "Show newer"; }
      return;
    }
    const t = e.target as HTMLElement | null;
    const row = t?.closest<HTMLElement>("[data-row]");

    /* MINIMIZE / OPEN one card. */
    const mn = t?.closest<HTMLButtonElement>("button[data-min]");
    if (mn && row) { setMin(row, !row.classList.contains("cd-min")); return; }
    /* A tap on a minimized card's name line opens it. */
    if (row?.classList.contains("cd-min") && !t?.closest("a,button")) { setMin(row, false); return; }

    /* LEAD: on the Leads tab, or off it. */
    const ld = t?.closest<HTMLButtonElement>("button[data-lead]");
    if (ld && row) {
      const p10 = ld.dataset["lead"] ?? "";
      const on = ld.dataset["on"] === "1";
      ld.disabled = true;
      try {
        await post("/api/calldesk/lead", { phone10: p10, undo: on });
        if (on && leadsOnly) { row.remove(); return; }
        ld.outerHTML = leadBtn(p10, !on);
        const pill = row.querySelector("[data-lead-pill]");
        if (on) pill?.remove();
        else if (!pill) row.querySelector("[data-head] b")?.insertAdjacentHTML("afterend", ` <span class="pill ok" data-lead-pill>Lead</span>`);
      } catch (err) { ld.disabled = false; alert(err instanceof Error ? err.message : String(err)); }
      return;
    }

    /* REPORT SPAM: blocks the number and takes the row off the list. */
    const sp = t?.closest<HTMLButtonElement>("button[data-spam]");
    if (sp && row) {
      const p10 = sp.dataset["spam"] ?? "";
      if (!confirm(`Report ${phoneFmt(p10)} as spam? The number is blocked: it leaves this list and gets no texts or alerts.`)) return;
      sp.disabled = true;
      try {
        await post("/api/calldesk/spam", { phone10: p10 });
        row.innerHTML = `<div class="row" style="justify-content:space-between;gap:.5rem;align-items:center">
          <span class="fine">${esc(phoneFmt(p10))} is blocked as spam.</span>
          <button type="button" class="btn ghost sm" data-unspam="${esc(p10)}">Undo</button></div>`;
        row.style.cursor = "";
      } catch (err) { sp.disabled = false; alert(err instanceof Error ? err.message : String(err)); }
      return;
    }
    const un = t?.closest<HTMLButtonElement>("button[data-unspam]");
    if (un) {
      un.disabled = true;
      try { await post("/api/calldesk/spam", { phone10: un.dataset["unspam"], undo: true }); await draw(); }
      catch { un.disabled = false; }
      return;
    }

    /* SEND TO NK: the caller's name, number and notes are texted to her. */
    const hb = t?.closest<HTMLButtonElement>("button[data-handoff]");
    if (hb) {
      const p10 = hb.dataset["handoff"] ?? "";
      const label = handoff ?? "Send";
      if (!confirm(`${label}? They get a text with ${phoneFmt(p10)} and this caller's notes, to call them.`)) return;
      hb.disabled = true; hb.textContent = "Sending…";
      try {
        /* A note typed but not saved yet goes in the text too. */
        const nb = row?.querySelector<HTMLElement>("[data-notes-for]");
        if (nb) await saveNote(nb);
        await post("/api/calldesk/handoff", { phone10: p10, name: hb.dataset["name"] ?? "" });
        hb.textContent = `${label.replace(/^Send/, "Sent")} ✓`;
      } catch (err) { hb.textContent = label; alert(err instanceof Error ? err.message : String(err)); }
      hb.disabled = false;
      return;
    }

    /* SAVE NOTES */
    const sv = t?.closest<HTMLButtonElement>("button[data-save-note]");
    const svBox = sv?.closest<HTMLElement>("[data-notes-for]");
    if (svBox) { await saveNote(svBox); return; }

    /* PAYMENTS (payHtml above). */
    const payBox = t?.closest<HTMLElement>("[data-pay-for]");
    if (payBox) { await payClick(payBox, t!); return; }

    /* STAFF (src/pages/calldesk-staff.ts, Neelum 2026-10-21). */
    const staffBox = t?.closest<HTMLElement>("[data-staff-for]");
    if (staffBox) { if (t?.closest("button")) await staffClick(staffBox, t); return; }

    /* CONTACT: one box open at a time, like the agreement card. */
    const ct = t?.closest<HTMLButtonElement>("button[data-contact]");
    if (ct) {
      const box = body.querySelector<HTMLElement>(`[data-contact-for="${CSS.escape(ct.dataset["contact"] ?? "")}"]`);
      if (!box) return;
      const open = !box.hidden;
      body.querySelectorAll<HTMLElement>(".calldesk-contact").forEach((h) => { h.hidden = true; h.innerHTML = ""; });
      if (!open) void mountContact(box, ct.dataset["phone"] ?? "", ct.dataset["name"] ?? "");
      return;
    }

    const b = t?.closest<HTMLButtonElement>("button[data-agree]");
    if (!b) {
      /* TAPPING A CALL STILL DOES SOMETHING (Neelum, 2026-10-15: "I'm
         tapping on Esmeralda colon ... nothing happens"). The notes box is
         open already, so a tap anywhere else on the row puts the cursor in it. */
      if (!row || t?.closest("a,button,input,textarea,audio,.callwrap,[data-notes-for],.calldesk-esign,.calldesk-contact,.calldesk-pay,.cd-staff")) return;
      row.querySelector("textarea")?.focus();
      return;
    }
    const host = body.querySelector<HTMLElement>(`[data-esign-for="${CSS.escape(b.dataset["agree"] ?? "")}"]`);
    if (!host) return;
    const wasOpen = !host.hidden;
    body.querySelectorAll<HTMLElement>(".calldesk-esign").forEach((h) => { h.hidden = true; h.innerHTML = ""; });
    if (wasOpen) return;
    void mountEsignForCaller(host, b.dataset["phone"] ?? "", b.dataset["name"] ?? "");
  });

  /* Leaving a notes box with something changed in it saves it. Not when
     the way out is that box's own Save button, which saves it anyway. */
  body?.addEventListener("focusout", (e) => {
    const ta = e.target as HTMLElement | null;
    if (!(ta instanceof HTMLTextAreaElement)) return;
    const box = ta.closest<HTMLElement>("[data-notes-for]");
    if (!box || ta.value === (ta.dataset["noteWas"] ?? "")) return;
    const to = (e as FocusEvent).relatedTarget as HTMLElement | null;
    if (to && box.contains(to) && to.matches("button[data-save-note]")) return;
    void saveNote(box);
  });

  /* An amount saves when the box is left. */
  body?.addEventListener("change", (e) => {
    const inp = e.target as HTMLElement | null;
    if (!(inp instanceof HTMLInputElement) || !inp.matches("[data-pay-amt]")) return;
    const box = inp.closest<HTMLElement>("[data-pay-for]");
    if (box) void payAmount(box, inp);
  });
  /* A logistics field saves when it is changed and left. */
  body?.addEventListener("change", (e) => {
    const f = e.target as HTMLElement | null;
    const box = f?.matches("[data-logi]") ? f.closest<HTMLElement>("[data-staff-for]") : null;
    if (box) void saveLogistics(box);
  });

  /* Above the list, not under it: at the foot it sat under forty callers
     and Neelum could not find it (2026-10-17, "Don't see it"). */
  const pay = document.getElementById("cdpay");
  if (pay) void payInfoEditor(pay);
  const team = document.getElementById("cdstaff");
  if (team) void staffEditor(team);
  await draw();
}

/* One card down to its name line, or back open; remembered by caller. */
function setMin(row: HTMLElement, min: boolean): void {
  const id = row.dataset["row"] ?? "";
  row.classList.toggle("cd-min", min);
  if (min) minimized.add(id); else minimized.delete(id);
  keepMin();
  const b = row.querySelector<HTMLButtonElement>("button[data-min]");
  if (b) { b.innerHTML = min ? "+" : "&minus;"; b.title = min ? "Open" : "Minimize";
    b.setAttribute("aria-label", `${min ? "Open" : "Minimize"} this caller`); b.setAttribute("aria-expanded", String(!min)); }
}

const payMsg = (box: HTMLElement, s: string) => { const m = box.querySelector<HTMLElement>("[data-pay-msg]"); if (m) m.textContent = s; };
const redrawPay = (box: HTMLElement) => { box.innerHTML = payHtml(box.dataset["payFor"] ?? ""); };

/* The line a control belongs to, made on the server first if it is the blank one. */
async function payLine(box: HTMLElement, line: HTMLElement): Promise<Payment | null> {
  const st = payState.get(box.dataset["payFor"] ?? "");
  if (!st) return null;
  const id = line.dataset["payLine"] ?? "";
  if (id) return st.payments.find((x) => x.id === id) ?? null;
  const out = await post("/api/calldesk/payment", { phone10: box.dataset["payFor"], add: true }) as { payment?: Payment };
  if (!out.payment) return null;
  st.payments.push(out.payment);
  line.dataset["payLine"] = out.payment.id;
  return out.payment;
}

async function payClick(box: HTMLElement, t: HTMLElement): Promise<void> {
  const p10 = box.dataset["payFor"] ?? "";
  const st = payState.get(p10);
  if (!st) return;
  try {
    const send = t.closest<HTMLButtonElement>("button[data-pay-send]");
    if (send) {
      const m = methods.find((x) => x.key === send.dataset["paySend"]);
      if (!m) return;
      if (!m.text) { alert(`${m.label} is not set up yet. An admin adds it under Payment info at the top of Calls.`); return; }
      const again = st.sent.some((x) => x.method === m.key);
      const to = reachOf(p10);
      if (to.cc !== "1") { alert(`Texts only go to +1 numbers. Their number is ${showNumber(to.cc, to.phone)}: open Contact and email the ${m.label} info instead.`); return; }
      if (!confirm(`${again ? "Send again: text" : "Text"} the ${m.label} payment info to ${st.name || phoneFmt(to.phone)}?\n\n${m.text}`)) return;
      send.disabled = true; payMsg(box, "Sending…");
      await post("/api/sms/thread", { phone: to.phone, via: p10, message: m.text });
      const out = await post("/api/calldesk/pay/sent", { phone10: p10, method: m.key }) as { id?: string; sent_at?: string };
      if (!again && out.id) st.sent.push({ id: out.id, method: m.key, sent_at: out.sent_at ?? new Date().toISOString() });
      redrawPay(box); payMsg(box, `${m.label} sent.`);
      return;
    }
    const undo = t.closest<HTMLButtonElement>("button[data-pay-undo]");
    if (undo) {
      if (!confirm("Undo this? The green mark comes off so you can send the right one. The text that already went cannot be taken back.")) return;
      await post("/api/calldesk/pay/undo", { id: undo.dataset["payUndo"] });
      st.sent = st.sent.filter((x) => x.id !== undo.dataset["payUndo"]);
      redrawPay(box); return;
    }
    const ok = t.closest<HTMLInputElement>("input[data-pay-ok]");
    if (ok) {
      const line = ok.closest<HTMLElement>("[data-pay-line]")!;
      if (!ok.checked && !confirm("Undo this payment confirmation?")) { ok.checked = true; return; }
      const p = await payLine(box, line);
      if (!p) return;
      const out = await post("/api/calldesk/payment", { id: p.id, confirmed: ok.checked }) as { payment?: Payment };
      if (out.payment) Object.assign(p, out.payment);
      redrawPay(box); return;
    }
    if (t.closest("button[data-pay-add]")) {
      /* The blank line on a caller with none yet becomes the first one. */
      for (let n = st.payments.length ? 1 : 2; n > 0; n--) {
        const out = await post("/api/calldesk/payment", { phone10: p10, add: true }) as { payment?: Payment };
        if (out.payment) st.payments.push(out.payment);
      }
      redrawPay(box); return;
    }
  } catch (err) {
    payMsg(box, err instanceof Error ? err.message : String(err));
    box.querySelectorAll<HTMLButtonElement>("button").forEach((b) => { b.disabled = false; });
  }
}

async function payAmount(box: HTMLElement, inp: HTMLInputElement): Promise<void> {
  if (inp.value === (inp.dataset["was"] ?? "")) return;
  try {
    const p = await payLine(box, inp.closest<HTMLElement>("[data-pay-line]")!);
    if (!p) return;
    const out = await post("/api/calldesk/payment", { id: p.id, amount: inp.value }) as { payment?: Payment };
    if (out.payment) Object.assign(p, out.payment);
    inp.dataset["was"] = inp.value; payMsg(box, "Amount saved.");
  } catch (err) { payMsg(box, err instanceof Error ? err.message : String(err)); }
}
